import type { CSSProperties, ElementType, ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface HoverProps {
  /** Tag to render when neither `to` nor `href` is set. Defaults to 'div'. */
  as?: ElementType;
  /** Internal route → react-router <Link>. */
  to?: string;
  /** Hash anchor / external / mailto → plain <a>. */
  href?: string;
  style?: CSSProperties;
  /** Extra style merged in while hovered (translates the design's `style-hover`). */
  hoverStyle?: CSSProperties;
  className?: string;
  type?: 'button' | 'submit';
  target?: string;
  rel?: string;
  onClick?: () => void;
  children?: ReactNode;
}

/**
 * Renders an element whose inline style gains `hoverStyle` on hover — the React
 * equivalent of the design build's `style-hover` attribute. Inline styles win
 * over the stylesheet, so the hover state overrides base values as intended.
 */
export default function Hover({
  as,
  to,
  href,
  style,
  hoverStyle,
  className,
  type,
  target,
  rel,
  onClick,
  children,
}: HoverProps) {
  const [hovered, setHovered] = useState(false);
  const merged = hovered && hoverStyle ? { ...style, ...hoverStyle } : style;

  const handlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onClick,
  };

  if (to) {
    return (
      <Link to={to} style={merged} className={className} {...handlers}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} style={merged} className={className} target={target} rel={rel} {...handlers}>
        {children}
      </a>
    );
  }

  const Tag = as || 'div';
  return (
    <Tag style={merged} className={className} type={type} {...handlers}>
      {children}
    </Tag>
  );
}
